import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { hrApi } from '../../api/hr.js';

const TABS = ['Overview', 'Documents', 'Attendance', 'Payroll', 'Performance'];

export default function EmployeeProfile() {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [error, setError] = useState(null);

  useEffect(() => {
    hrApi
      .getEmployee(id)
      .then((d) => setEmployee(d?.employee))
      .catch((e) => setError(e.response?.data?.message || 'Failed'));
  }, [id]);

  if (error) return <div className="alert error">{error}</div>;
  if (!employee) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="profile-header card">
        <div className="avatar lg">{(employee.user_name || '?').charAt(0).toUpperCase()}</div>
        <div className="meta">
          <h1>{employee.user_name}</h1>
          <p className="muted">{employee.position_title || '—'} · {employee.department_name || 'No department'}</p>
          <div className="chips">
            <span className="chip">{employee.employee_code}</span>
            <span className={`chip status-${employee.status}`}>{employee.status}</span>
            <span className="chip">{employee.employment_type}</span>
          </div>
        </div>
      </header>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      <div className="tab-panel">
        {tab === 'Overview' && <Overview employee={employee} />}
        {tab === 'Documents' && <Documents employeeId={id} />}
        {tab === 'Attendance' && <Attendance employeeId={id} />}
        {tab === 'Payroll' && <Payroll employeeId={id} />}
        {tab === 'Performance' && <Performance employeeId={id} />}
      </div>
    </section>
  );
}

function Overview({ employee }) {
  const [skills, setSkills] = useState([]);
  useEffect(() => {
    hrApi.listSkills(employee.id).then((d) => setSkills(d?.items || [])).catch(() => {});
  }, [employee.id]);

  return (
    <div className="card">
      <h3>Personal</h3>
      <dl className="kv">
        <dt>Email</dt><dd>{employee.user_email}</dd>
        <dt>Phone</dt><dd>{employee.phone || '—'}</dd>
        <dt>Emergency Contact</dt><dd>{employee.emergency_contact || '—'}</dd>
        <dt>Hire Date</dt><dd>{employee.hire_date}</dd>
        <dt>Birth Date</dt><dd>{employee.birth_date || '—'}</dd>
      </dl>
      <h3>Skills</h3>
      <ul className="chips">
        {skills.length === 0 && <li className="muted">No skills recorded.</li>}
        {skills.map((s) => (
          <li key={s.id} className="chip">
            {s.name} <span className="muted small">· level {s.proficiency_level}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Documents({ employeeId }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  function load() {
    hrApi.listDocuments(employeeId).then((d) => setItems(d?.items || [])).catch((e) => setError(e.message));
  }
  useEffect(load, [employeeId]);

  async function onUpload(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const file = fd.get('file');
    if (!file || file.size === 0) return;
    try {
      await hrApi.uploadDocument(employeeId, file, {
        type: fd.get('type') || 'other',
        name: fd.get('name'),
        expiryDate: fd.get('expiryDate') || null,
      });
      e.currentTarget.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    }
  }

  return (
    <div className="card">
      <h3>Documents</h3>
      <form onSubmit={onUpload} className="row-form">
        <input name="name" placeholder="Display name" />
        <select name="type" defaultValue="contract">
          <option value="contract">Contract</option>
          <option value="id">ID</option>
          <option value="certificate">Certificate</option>
          <option value="resume">Resume</option>
          <option value="other">Other</option>
        </select>
        <input name="expiryDate" type="date" />
        <input name="file" type="file" required />
        <button type="submit" className="btn primary">Upload</button>
      </form>
      {error && <div className="alert error">{error}</div>}
      <table className="data-table">
        <thead><tr><th>Name</th><th>Type</th><th>Expires</th><th>Uploaded</th><th></th></tr></thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td>{d.name}</td>
              <td>{d.type}</td>
              <td>{d.expiry_date || '—'}</td>
              <td className="muted">{d.uploaded_at?.slice(0, 10)}</td>
              <td><a href={d.file_url} target="_blank" rel="noreferrer">Download</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Attendance({ employeeId }) {
  const [items, setItems] = useState([]);
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  useEffect(() => {
    hrApi.timesheet({ employee_id: employeeId, date_from: monthStart }).then((d) => setItems(d?.items || []));
  }, [employeeId, monthStart]);

  return (
    <div className="card">
      <h3>This Month</h3>
      <AttendanceHeatmap items={items} />
      <table className="data-table">
        <thead><tr><th>Date</th><th>Clock-in</th><th>Clock-out</th><th>Hours</th><th>Source</th></tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id}>
              <td>{r.date}</td>
              <td>{r.clock_in?.slice(11, 16) || '—'}</td>
              <td>{r.clock_out?.slice(11, 16) || '—'}</td>
              <td>{r.hours_worked ?? '—'}</td>
              <td className="muted">{r.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AttendanceHeatmap({ items }) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const days = new Date(year, month + 1, 0).getDate();
  const map = new Map(items.map((i) => [i.date, i.hours_worked || 0]));
  const cells = Array.from({ length: days }, (_, i) => {
    const day = i + 1;
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const h = map.get(date) ?? null;
    let level = 0;
    if (h != null) level = h >= 8 ? 4 : h >= 6 ? 3 : h >= 4 ? 2 : h > 0 ? 1 : 0;
    return { day, date, h, level };
  });
  return (
    <div className="heatmap" title="Daily hours worked">
      {cells.map((c) => (
        <div key={c.date} className={`hm-cell lvl-${c.level}`} title={`${c.date}: ${c.h ?? 'no record'}h`}>
          <span>{c.day}</span>
        </div>
      ))}
    </div>
  );
}

function Payroll({ employeeId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    hrApi.payslipsForEmployee(employeeId).then((d) => setItems(d?.items || []));
  }, [employeeId]);
  return (
    <div className="card">
      <h3>Payslips</h3>
      <table className="data-table">
        <thead><tr><th>Period</th><th>Gross</th><th>Tax</th><th>Net</th><th></th></tr></thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <td>{p.period_year}-{String(p.period_month).padStart(2, '0')}</td>
              <td>{p.currency} {Number(p.gross).toFixed(2)}</td>
              <td>{p.currency} {Number(p.tax).toFixed(2)}</td>
              <td>{p.currency} {Number(p.net).toFixed(2)}</td>
              <td><a href={p.file_url} target="_blank" rel="noreferrer">PDF</a></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="muted">No payslips yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Performance({ employeeId }) {
  const [reviews, setReviews] = useState([]);
  const [training, setTraining] = useState([]);
  useEffect(() => {
    hrApi.reviews({ employee_id: employeeId }).then((d) => setReviews(d?.items || []));
    hrApi.trainingForEmployee(employeeId).then((d) => setTraining(d?.items || []));
  }, [employeeId]);

  return (
    <div className="card">
      <h3>Reviews</h3>
      {reviews.length === 0 && <div className="muted">No reviews yet.</div>}
      <ul className="review-list">
        {reviews.map((r) => (
          <li key={r.id}>
            <div className="row">
              <strong>{r.period}</strong>
              <span className={`chip status-${r.status}`}>{r.status}</span>
              {r.rating && <span className="chip">★ {r.rating}/5</span>}
            </div>
            {r.strengths && <p><strong>Strengths:</strong> {r.strengths}</p>}
            {r.improvements && <p><strong>Areas to improve:</strong> {r.improvements}</p>}
            {r.comments && <p className="muted">{r.comments}</p>}
          </li>
        ))}
      </ul>
      <h3>Training</h3>
      {training.length === 0 && <div className="muted">No training records.</div>}
      <table className="data-table">
        <thead><tr><th>Course</th><th>Provider</th><th>Status</th><th>Completed</th></tr></thead>
        <tbody>
          {training.map((t) => (
            <tr key={t.id}>
              <td>{t.course_name}</td>
              <td>{t.provider || '—'}</td>
              <td><span className={`chip status-${t.status}`}>{t.status}</span></td>
              <td>{t.completed_at?.slice(0, 10) || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
